/**
 * Navigation enhancements: sticky-header styling, scroll-spy active link,
 * and accessibility for the (pure-CSS) mobile drawer. The drawer opens and
 * closes without JavaScript via a checkbox; here we only add aria state,
 * Escape-to-close, and close-on-link-tap.
 */
export function initNav(): void {
  initHeaderState();
  initScrollSpy();
  initMobileDrawer();
}

function initHeaderState(): void {
  const header = document.getElementById('site-header');
  if (!header) return;

  const solid = ['bg-oat/85', 'backdrop-blur-md', 'shadow-header', 'border-gold/15'];
  const update = () => {
    if (window.scrollY > 24) header.classList.add(...solid);
    else header.classList.remove(...solid);
  };

  update();
  window.addEventListener('scroll', update, { passive: true });
}

function initScrollSpy(): void {
  const links = Array.from(document.querySelectorAll<HTMLAnchorElement>('[data-nav]'));
  if (links.length === 0 || !('IntersectionObserver' in window)) return;

  const byId = new Map<string, HTMLAnchorElement>();
  links.forEach((link) => {
    const id = link.getAttribute('href')?.slice(1);
    if (id) byId.set(id, link);
  });

  const sections = Array.from(byId.keys())
    .map((id) => document.getElementById(id))
    .filter((el): el is HTMLElement => el !== null);

  const setActive = (id: string) => {
    links.forEach((link) => link.classList.remove('is-active'));
    byId.get(id)?.classList.add('is-active');
  };

  const observer = new IntersectionObserver(
    (entries) => {
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (visible?.target.id) setActive(visible.target.id);
    },
    { rootMargin: '-45% 0px -50% 0px', threshold: [0, 0.25, 0.5] }
  );

  sections.forEach((section) => observer.observe(section));
}

function initMobileDrawer(): void {
  const toggle = document.getElementById('nav-toggle') as HTMLInputElement | null;
  const openBtn = document.getElementById('nav-open');
  const drawerLinks = Array.from(document.querySelectorAll<HTMLAnchorElement>('[data-drawer-link]'));
  if (!toggle) return;

  const syncAria = () => {
    openBtn?.setAttribute('aria-expanded', String(toggle.checked));
  };

  toggle.addEventListener('change', syncAria);
  syncAria();

  // Close when a drawer link is tapped so the anchor jump is unobstructed.
  drawerLinks.forEach((link) =>
    link.addEventListener('click', () => {
      toggle.checked = false;
      syncAria();
    })
  );

  // Escape closes the drawer and returns focus to the opener.
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && toggle.checked) {
      toggle.checked = false;
      syncAria();
      openBtn?.focus();
    }
  });
}
